export interface MonthlyAmount {
  year: number;
  month: number;
  amount: number;
}

export type TeamMemberType = 'Engineer' | 'Draftsperson' | 'Accounts';

export interface ProjectTeamMember {
  id: string;
  first_name: string;
  last_name: string;
  type: TeamMemberType | string;
}

export interface BudgetRow {
  entity_type: 'project' | 'stage';
  project_id: number;
  stage_id: number | null;
  job_number: number | null;
  project_name: string;
  stage_code: string | null;
  stage_description: string | null;
  project_type: string;
  project_lead_name: string | null;
  workflow_status_name: string | null;
  /** Client company name. A project property; stage rows show their parent's. */
  client_name: string | null;
  // entity_fee is the single per-row fee — project_total_fee/stage_fee are
  // kept as duplicates of entity_fee for backwards compatibility.
  entity_fee: number;
  project_total_fee: number;
  stage_fee: number | null;
  /**
   * The slice of entity_fee that is quoted but not awarded, and so excluded
   * from unallocated_fee. Either the WHOLE fee (the entity's own workflow
   * status is Probable) or the sum of its fee sections flagged is_probable —
   * the status wins. Computed in get_budget_page_data.
   */
  probable_fee: number;
  /**
   * entity_fee minus probable_fee: the scope actually awarded. This is the
   * figure the page shows as "Won Fee", and the one unallocated_fee is
   * measured against. Returned by the RPC rather than subtracted here so the
   * two can't disagree.
   */
  won_fee: number;
  total_invoiced_all_time: number;
  total_forecast_all_time: number;
  // Pre-FY tracking lump sum (stored as month=0 sentinel). Only displayed
  // on the FY25/26 view; the all-time-sum already includes it in
  // unallocated_fee on every FY.
  prior_amount: number;
  unallocated_fee: number;
  team_members: ProjectTeamMember[];
  forecasts: MonthlyAmount[];
  actuals: MonthlyAmount[];
}

export interface MonthlyBudgetSlot {
  year: number;
  month: number;
  budget_amount: number;
  is_inherited: boolean;
}

export interface BudgetAggregateRow {
  id: number;
  row_type: 'rwc_all' | 'rwd_client';
  client_id: number | null;
  label: string;
  project_count: number;
  /** Editable annual budget (Total Fee column) for the displayed FY. */
  total_fee: number;
  /** total_fee minus the sum of this FY's monthly targets. */
  unallocated_fee: number;
  actuals: MonthlyAmount[];
  targets: MonthlyAmount[];
}

export interface BudgetPageData {
  rows: BudgetRow[];
  /**
   * Rows per PROJECT workflow status, honouring every filter except the
   * status one — so each quick-filter pill can say how many rows it would
   * show. Keyed by status name ('' for a project with no status).
   */
  status_counts?: Record<string, number>;
  monthly_budget: MonthlyBudgetSlot[];
  aggregate_rows: BudgetAggregateRow[];
}

export interface BudgetFilters {
  fyStartYear: number;
  projectTypes: string[];
  projectLead: string;
  workflowStatuses: string[];
  clientId: string;
  search: string;
}

export const PROJECT_TYPE_OPTIONS = ['Design', 'Advisory'] as const;

export const CELL_SAVE_DEBOUNCE_MS = 900;

// Terminal lifecycle statuses. The single client-side source: feeds the
// Complete quick-filter pill, the zero-unallocated rule, and the probable
// split below. Must match v_terminal in get_budget_page_data
// (supabase/schemas/09_budget/functions.sql) — the only other copy.
export const TERMINAL_STATUSES = ['Complete', 'Archived', 'Unsuccessful'];

/**
 * How much of a fee is probable (quoted, not awarded), given the entity's own
 * workflow status and the sum of its sections flagged is_probable. The status
 * wins: a finished job has none, a job still AT status Probable is wholly
 * probable whatever its sections say. Mirrors the rows_with_probable CTE in
 * get_budget_page_data — the job card Overview uses this so its Won fee is the
 * same number as the Budget row it was opened from.
 */
export const resolveProbableFee = (
  workflowStatus: string | null | undefined,
  fee: number,
  flaggedProbable: number,
): number => {
  if (TERMINAL_STATUSES.includes(workflowStatus ?? '')) return 0;
  if (workflowStatus === 'Probable') return fee;
  return Math.min(flaggedProbable, fee);
};
